import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js';
import axiosEvents from '../config/axiosInstanceEvents';
import BillingActiveMessage from './BillingActiveMessage';
import AppNotInstalledMessage from './AppNotInstalledMessage';
import LoadingSpinner from './LoadingSpinner';

// Use the live Stripe key for now (same as teams-pagebilling.js)
// const stripePromise = loadStripe('pk_test_aubx4QrCXgh7gmaqmyjx5jHQ00u6EtZOmX');

// For full live for production - use this (with fallback to prevent undefined)
const stripePromise = loadStripe('pk_live_oYyuovMeNVaQe5SSflWCE8XD00edZonWk9');

const BillingDashboard = ({ user }) => {
  const { teamsuser, teamsloading } = useSelector(state => state.teamsAuth);
  const [planDuration, setPlanDuration] = useState('Yearly');
  const [loading, setLoading] = useState(false);
  const [initRef, setInitRef] = useState('null');
  const [initType, setInitType] = useState('null');

  // Add this useEffect to handle page visibility and reset loading state
  useEffect(() => {
    // Reset loading state when component mounts
    setLoading(false);

    // Extract URL parameters
    let search = window.location.search;
    let params = new URLSearchParams(search);
    let ref = params.get('ref');
    let firstType = params.get('type');
    setInitRef(ref === null ? 'null' : ref);
    setInitType(firstType === null ? 'null' : firstType);

    // Handle page visibility change (when user comes back from Stripe)
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        // Page is visible again, reset loading state
        setLoading(false);
      }
    };

    // Handle focus event (when user returns to tab)
    const handleFocus = () => {
      setLoading(false);
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  const handleClickPrice = async (planType, duration, level) => {
    setLoading(true);

    try {
      const stripe = await stripePromise;
      
      // Get user context from Redux state
      const userContext = teamsuser?.user || {};
      
      let priceId;
      let numItems = 1;

      // Price mapping based on teams-pagebilling.js
      // For full live change the starter monthly price from "price_1L8NkHJBEzpLhH7BuisbDCcW" to 'price_1Q7GGAJBEzpLhH7BZhO7QoMb' (real one)
      // delete below line also
      var prevLeChannelId = userContext.tenantId || 'unknown'
      if (level === "Starter") {
        priceId = duration === "Monthly" ? 'price_1Q7GGAJBEzpLhH7BZhO7QoMb' : 'price_1Q7GFgJBEzpLhH7B26c57kyV';
      } else if (level === "Basic") {
        priceId = duration === "Monthly" ? 'price_1LfhSCJBEzpLhH7BHamNIxKp' : 'price_1LfhSbJBEzpLhH7BYIOOSu0x';
      } else if (level === "Growth") {
        priceId = duration === "Monthly" ? 'price_1L82MdJBEzpLhH7BUXYCfrMz' : 'price_1L82MdJBEzpLhH7B0S93cSSR';
      }

      const body = {
        priceId,
        type: planType,
        duration: duration,
        numItems,
        teamId: userContext.teamId || 'unknown',
        tenantId: userContext.tenantId || 'unknown',
        channelId: userContext.channelId || 'unknown',
        clientRefStuff: "None",
        initRef: initRef,
        initType: initType
      };

      const configT = {
        headers: {
            'Content-Type': 'application/json',
        }
      };

      const res = await axiosEvents.post('/api/checkoutSessionNew/newteamssub', body, configT);
      const sessionId = res.data;

      // Don't reset loading here since we're redirecting
      const { error } = await stripe.redirectToCheckout({
        sessionId: sessionId
      });

      if (error) {
        console.error('Stripe error:', error);
        setLoading(false); // Only reset on error
      }

    } catch (error) {
      console.error('Checkout error:', error);
      alert('There was an error processing your request. Please try again.');
      setLoading(false); // Reset on error
    }
    // Removed the finally block since we handle loading reset in useEffect
  };

  const plans = [
    {
      name: "Starter",
      level: "Starter",
      monthly: { price: 34, priceYear: 288 },
      users: "Up to 40 users",
      features: [
        "Unlimited games in your entire organization",
        "All Trivia",
        "All Water Cooler Games", 
        "All 2-player Games",
        "All Icebreaker Games",
        "All Social Games"
      ]
    },
    {
      name: "Basic", 
      level: "Basic",
      monthly: { price: 59, priceYear: 480 },
      users: "Up to 100 users",
      features: [
        "Unlimited games in your entire organization",
        "All Trivia",
        "All Water Cooler Games",
        "All 2-player Games", 
        "All Icebreaker Games",
        "All Social Games"
      ]
    },
    {
      name: "Growth",
      level: "Growth", 
      monthly: { price: 99, priceYear: 948 },
      users: "Up to 250 users",
      features: [
        "Unlimited games in your entire organization",
        "All Trivia",
        "All Water Cooler Games",
        "All 2-player Games",
        "All Icebreaker Games", 
        "All Social Games"
      ]
    },
    {
      name: "Enterprise",
      level: "Enterprise",
      monthly: { price: "Contact us", priceYear: "Contact us" },
      users: "Above 250 users", 
      features: [
        "Unlimited games in your entire organization",
        "All Trivia",
        "All Water Cooler Games",
        "All 2-player Games",
        "All Icebreaker Games",
        "All Social Games",
        "Priority Support"
      ],
      isEnterprise: true
    }
  ];

  // Check if user has active billing and if app is installed (same pattern as Slack BillingPrices.js line 555)
  const userContext = teamsuser?.user || {};
  const isBillingActive = userContext.isBillingActive === true;
  const isAppInstalled = userContext.evenInstalled !== false; // Default to true if undefined

  return (
    teamsloading ? <LoadingSpinner /> : 
    teamsloading === false && !isAppInstalled ? <AppNotInstalledMessage user={userContext} /> :
    teamsloading === false && isBillingActive === true ? <BillingActiveMessage user={userContext} /> :
    <div className="billing-dashboard">
      <div className="container" style={{ 
        marginTop: window.innerWidth <= 768 ? '2rem' : '3.5rem',
        paddingLeft: window.innerWidth <= 768 ? '1rem' : '15px',
        paddingRight: window.innerWidth <= 768 ? '1rem' : '15px'
      }}>
        <div className="billing-header">
          <h2 style={{ 
            color: '#E04F60', 
            textAlign: 'center', 
            marginBottom: '0.5rem',
            fontSize: '2.2rem',
            fontWeight: '700',
            letterSpacing: '-0.025em'
          }}>
            Choose Your Plan
          </h2>
          <h4 style={{
            color: '#2C3E50',
            textAlign: 'center',
            marginBottom: '0.75rem',
            fontSize: '1.1rem',
            fontWeight: '400'
          }}>
            Ricotta Games, Trivia & Icebreakers for Microsoft Teams
          </h4>
          <p className="text-center" style={{ 
            color: '#6c757d', 
            marginBottom: '2rem',
            fontSize: '0.95rem',
            maxWidth: '600px',
            margin: '0 auto 3rem auto'
          }}>
            For <strong>Enterprise plans</strong> or custom pricing, reach out to us at <span style={{ color: '#E04F60' }}>cheese@ricotta.team</span>
          </p>
        </div>

        {/* Duration Toggle */}
        <div className="d-flex justify-content-center mb-5">
          <div className="btn-group shadow-sm" role="group" aria-label="Plan duration" style={{
            borderRadius: '50px',
            overflow: 'hidden',
            border: '2px solid #E04F60'
          }}>
            <button
              type="button"
              onClick={() => setPlanDuration('Monthly')}
              style={{
                backgroundColor: planDuration === 'Monthly' ? '#E04F60' : 'transparent',
                border: 'none',
                color: planDuration === 'Monthly' ? 'white' : '#E04F60',
                padding: '12px 32px',
                fontSize: '0.95rem',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setPlanDuration('Yearly')}
              style={{
                backgroundColor: planDuration === 'Yearly' ? '#E04F60' : 'transparent',
                border: 'none',
                color: planDuration === 'Yearly' ? 'white' : '#E04F60',
                padding: '12px 32px',
                fontSize: '0.95rem',
                fontWeight: '600',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
            >
              Yearly <span style={{ 
                fontSize: '0.8rem', 
                backgroundColor: planDuration === 'Yearly' ? 'rgba(255,255,255,0.2)' : 'rgba(224,79,96,0.1)',
                padding: '2px 8px',
                borderRadius: '12px',
                marginLeft: '8px'
              }}>Save upto 30%</span>
            </button>
          </div>
        </div>

        {/* Pricing Cards */}
        <section className="pricing py-2">
          <div className="container">
            <div className="row mt-1">
              {plans.map((plan, index) => (
                <div key={index} className="col-xl-3 col-lg-6 col-md-6 col-sm-12 mb-4">
                  <div className="card h-100" style={{ 
                    border: '1px solid #e8ecef',
                    borderRadius: '16px',
                    boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.3s ease',
                    overflow: 'hidden'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.transform = 'translateY(-8px)';
                    e.currentTarget.style.boxShadow = '0 20px 40px rgba(224, 79, 96, 0.15)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 8px 30px rgba(0, 0, 0, 0.08)';
                  }}>
                    <div className="card-body d-flex flex-column" style={{ padding: '2rem 1.5rem' }}>
                      <div className="text-center mb-3">
                        <h5 className="card-title text-uppercase" style={{ 
                          fontSize: '0.85rem', 
                          fontWeight: '700',
                          color: '#6c757d',
                          letterSpacing: '0.5px',
                          marginBottom: '0.5rem'
                        }}>
                          {plan.name}
                        </h5>
                        <div style={{ fontSize: '0.8rem', color: '#95a5a6', marginBottom: '0.5rem' }}>
                          {planDuration} billing
                        </div>
                      </div>
                      
                      <div className="text-center mb-4">
                        <div className="card-price" style={{ 
                          fontSize: plan.isEnterprise ? '2rem' : '3rem', 
                          fontWeight: '800', 
                          color: '#E04F60',
                          lineHeight: '1',
                          marginBottom: '0.25rem'
                        }}>
                          {plan.isEnterprise ? (
                            "Custom"
                          ) : (
                            <>
                              <span style={{ fontSize: '1.5rem', verticalAlign: 'top' }}>$</span>
                              {planDuration === "Monthly" ? plan.monthly.price : plan.monthly.priceYear}
                            </>
                          )}
                        </div>
                        {!plan.isEnterprise && (
                          <div style={{ fontSize: '0.9rem', color: '#6c757d', fontWeight: '500' }}>
                            per {planDuration === "Monthly" ? "month" : "year"}
                          </div>
                        )}
                      </div>
                      
                      <div style={{ 
                        borderTop: '1px solid #f1f3f4', 
                        paddingTop: '1.5rem',
                        marginBottom: '1.5rem'
                      }}>
                        <div style={{ 
                          fontSize: '0.9rem', 
                          fontWeight: '600', 
                          color: '#2C3E50',
                          marginBottom: '1rem',
                          textAlign: 'center'
                        }}>
                          {plan.users}
                        </div>
                        
                        <ul className="list-unstyled flex-grow-1" style={{ marginBottom: '0' }}>
                          {plan.features.map((feature, featureIndex) => (
                            <li key={featureIndex} style={{ 
                              marginBottom: '0.75rem',
                              fontSize: '0.85rem',
                              color: '#495057',
                              display: 'flex',
                              alignItems: 'flex-start'
                            }}>
                              <i className="fa fa-check" style={{ 
                                color: '#27ae60', 
                                marginRight: '0.75rem',
                                marginTop: '0.125rem',
                                fontSize: '0.8rem'
                              }}></i>
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="mt-auto">
                        {plan.isEnterprise ? (
                          <div className="text-center">
                            <button
                              style={{
                                backgroundColor: 'transparent',
                                border: '2px solid #E04F60',
                                color: '#E04F60',
                                fontWeight: '600',
                                padding: '14px 24px',
                                borderRadius: '50px',
                                fontSize: '0.9rem',
                                width: '100%',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                              }}
                              onMouseOver={(e) => {
                                e.target.style.backgroundColor = '#E04F60';
                                e.target.style.color = 'white';
                                e.target.style.transform = 'translateY(-2px)';
                              }}
                              onMouseOut={(e) => {
                                e.target.style.backgroundColor = 'transparent';
                                e.target.style.color = '#E04F60';
                                e.target.style.transform = 'translateY(0)';
                              }}
                              onClick={() => window.open('mailto:cheese@ricotta.team', '_blank')}
                            >
                              Contact us
                            </button>
                          </div>
                        ) : (
                          <button
                            style={{
                              background: 'linear-gradient(135deg, #E04F60 0%, #c94456 100%)',
                              border: 'none',
                              color: 'white',
                              fontWeight: '600',
                              padding: '16px 24px',
                              borderRadius: '50px',
                              fontSize: '0.95rem',
                              width: '100%',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              boxShadow: '0 4px 15px rgba(224, 79, 96, 0.3)'
                            }}
                            onClick={() => handleClickPrice('Org', planDuration, plan.level)}
                            disabled={loading}
                            onMouseOver={(e) => {
                              if (!loading) {
                                e.target.style.transform = 'translateY(-3px)';
                                e.target.style.boxShadow = '0 8px 25px rgba(224, 79, 96, 0.4)';
                              }
                            }}
                            onMouseOut={(e) => {
                              if (!loading) {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 4px 15px rgba(224, 79, 96, 0.3)';
                              }
                            }}
                          >
                            {loading ? (
                              <>
                                <i className="fa fa-refresh fa-spin" style={{ marginRight: "8px" }} />
                                Processing...
                              </>
                            ) : (
                              <>
                                Get Started
                                <i className="fa fa-arrow-right" style={{ marginLeft: "8px", fontSize: "0.8rem" }} />
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Enterprise Contact */}
            <div className="row mt-5">
              <div className="col-12 text-center">
                <div style={{
                  backgroundColor: 'rgba(224, 79, 96, 0.05)',
                  borderRadius: '16px',
                  padding: '2rem',
                  border: '1px solid rgba(224, 79, 96, 0.1)'
                }}>
                  <h5 style={{ 
                    color: '#2C3E50', 
                    marginBottom: '1rem',
                    fontWeight: '600',
                    fontSize: '1.1rem'
                  }}>
                    Non-Profits & Educational Institutions Discounts
                  </h5>
                  <p style={{ 
                    color: '#6c757d',
                    fontSize: '0.95rem',
                    marginBottom: '1.5rem',
                    maxWidth: '500px',
                    margin: '0 auto 1.5rem auto'
                  }}>
                    We offer special discounts to Educational Institutions and Non-Profits.
                  </p>
                  <a 
                    href="mailto:cheese@ricotta.team" 
                    style={{ 
                      color: '#E04F60',
                      textDecoration: 'none',
                      fontWeight: '600',
                      fontSize: '0.95rem',
                      borderBottom: '2px solid transparent',
                      transition: 'border-color 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.target.style.borderBottomColor = '#E04F60';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.borderBottomColor = 'transparent';
                    }}
                  >
                    Contact us at cheese@ricotta.team →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default BillingDashboard;